import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Target, Coins, Check, Sparkles, Lightbulb, Shuffle, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";

const BOOSTER_META = {
  hint: { icon: Lightbulb, key: "play.booster_coach_tip", fallback: "Coach Tip" },
  shuffle: { icon: Shuffle, key: "play.shuffle", fallback: "Shuffle" },
  undo: { icon: RotateCcw, key: "play.undo", fallback: "Undo" },
};

/**
 * Celebration overlay for claiming the 3/3 Daily Goal reward. Warm gold
 * card with a spring-in target ring, animated coin count and any booster
 * rewards. Auto-dismisses after ~3.4s; tap anywhere to close early.
 *
 * Props: open, coins, boosters {hint:1,...}, onDone
 */
export default function DailyGoalClaimCelebration({ open, coins = 0, boosters = {}, onDone }) {
  const { t } = useTranslation();
  const onDoneRef = React.useRef(onDone);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => onDoneRef.current?.(), 3400);
    return () => clearTimeout(timer);
  }, [open]);

  const boosterList = Object.entries(boosters || {}).filter(([, v]) => Number(v) > 0);

  return (
    <AnimatePresence>
      {open && (
        <div
          data-testid="daily-goal-claim-celebration"
          className="fixed inset-0 z-[205] flex items-center justify-center px-4 bg-[#2C332A]/50 backdrop-blur-[3px]"
          aria-live="polite"
          onClick={() => onDoneRef.current?.()}
        >
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 22 }}
            className="relative w-full max-w-[340px] rounded-3xl overflow-hidden shadow-2xl px-6 py-7 text-center"
            style={{
              background: "linear-gradient(150deg,#F2C078 0%,#E29A46 55%,#C97C2E 100%)",
              boxShadow: "0 22px 64px rgba(90,54,12,0.5), inset 0 2px 0 rgba(255,255,255,0.4)",
            }}
          >
            <motion.div
              initial={{ opacity: 0.4, scale: 0.9 }}
              animate={{ opacity: 0.85, scale: 1.06 }}
              transition={{ duration: 1.2, repeat: 1, repeatType: "reverse" }}
              className="absolute inset-0 pointer-events-none"
              style={{ background: "radial-gradient(ellipse at top, rgba(255,255,255,0.55), transparent 62%)" }}
            />
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 0, y: 0, scale: 0.6 }}
                animate={{ opacity: [0, 1, 0], x: (i % 2 === 0 ? -1 : 1) * (32 + i * 13), y: -22 - i * 11, scale: 1, rotate: 26 * (i - 2) }}
                transition={{ duration: 1.6, delay: 0.15 + i * 0.1 }}
                className="absolute top-7 left-1/2 -translate-x-1/2 text-white pointer-events-none"
              >
                <Sparkles size={16} strokeWidth={2.2} />
              </motion.div>
            ))}

            <p className="relative text-[11px] uppercase tracking-[0.22em] font-bold text-white/85 mb-3">
              {t("daily_goal.claim_title", "Daily goal complete")}
            </p>

            {/* Target ring with a check punch-in */}
            <motion.div
              initial={{ rotate: -20, scale: 0.5 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: "spring", stiffness: 240, damping: 13, delay: 0.05 }}
              className="relative w-24 h-24 mx-auto rounded-full bg-white/25 border-[3px] border-white grid place-items-center"
            >
              <Target size={44} strokeWidth={2} className="text-white" />
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 400, damping: 12, delay: 0.5 }}
                className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[#5F7161] border-2 border-white grid place-items-center text-white"
              >
                <Check size={18} strokeWidth={3} />
              </motion.span>
            </motion.div>

            <p style={{ fontFamily: "Outfit, sans-serif" }} className="relative text-lg font-semibold text-white mt-4">
              {t("daily_goal.claim_reward_header", "Reward claimed!")}
            </p>

            {/* Coins */}
            <div className="relative mt-3 inline-flex items-center gap-2 bg-white/25 rounded-full px-5 py-2 text-white">
              <Coins size={18} strokeWidth={2.2} />
              <span className="text-xl font-bold" data-testid="daily-goal-claim-coins">+{coins}</span>
              <span className="text-sm font-medium opacity-90">{t("play.coins", "coins")}</span>
            </div>

            {/* Boosters */}
            {boosterList.length > 0 && (
              <div className="relative mt-3 flex items-center justify-center gap-2 flex-wrap" data-testid="daily-goal-claim-boosters">
                {boosterList.map(([k, v]) => {
                  const meta = BOOSTER_META[k] || { icon: Sparkles, key: k, fallback: k };
                  const Icon = meta.icon;
                  return (
                    <span key={k} className="inline-flex items-center gap-1.5 bg-[#2C332A]/25 rounded-full px-3 py-1.5 text-white text-sm font-semibold">
                      <Icon size={14} strokeWidth={2} />
                      +{v} {t(meta.key, meta.fallback)}
                    </span>
                  );
                })}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
